/** Live number of open, non-snoozed Support conversations. */
import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export function useSupportUnreadCount(): number {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: openCount = 0 } = useQuery({
    queryKey: ['admin-issues', 'open-count'],
    queryFn: async () => {
      const now = new Date().toISOString()
      const { count, error } = await supabase
        .from('issues')
        .select('id', { count: 'exact', head: true })
        .in('status', ['open', 'in_progress', 'waiting'])
        .is('archived_at', null)
        .or(`snoozed_until.is.null,snoozed_until.lte.${now}`)
      if (error) throw error
      return count ?? 0
    },
    enabled: !!user?.id,
    staleTime: 15_000,
  })

  // Realtime: bump the count the moment a new message or ticket lands,
  // and re-fetch when an issue's status flips (since "todo" count was
  // historically part of the same surface).
  useEffect(() => {
    if (!user?.id) return
    const channel = supabase
      .channel('admin-support-unread')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'issue_comments' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['admin-issues', 'open-count'] })
        },
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'issues' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['admin-issues', 'open-count'] })
        },
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'issues' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['admin-issues', 'open-count'] })
        },
      )
      .subscribe()
    return () => {
      try {
        supabase.removeChannel(channel)
      } catch {
        /* ignore */
      }
    }
  }, [user?.id, queryClient])

  return openCount
}
